// frontend/src/components/common/Header.js - UPDATED with consistent branding
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Home, LogOut, User, ChevronDown, Plus, BookOpen, MessageSquare } from 'lucide-react';
import { auth, logOut } from '../../config/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import ThemeToggle from '../ui/ThemeToggle';

export default function Header() {
    const [user] = useAuthState(auth);
    const [showUserMenu, setShowUserMenu] = useState(false);

    const handleLogout = async () => {
        try {
            await logOut();
            setShowUserMenu(false);
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    return (
        <header className="bg-surface border-b border-line sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="flex items-center justify-between h-16">
                    <div className="flex items-center gap-6">
                        {/* Brand — wordmark only (no decorative AI emoji per
                            ui-design-system.md). */}
                        <Link to="/" className="text-xl font-bold text-ink hover:text-read transition-colors">
                            AdaptIQ
                        </Link>

                        {/* Top-nav. Tutor first — it's the primary surface;
                            My Courses + Create are utilities. */}
                        <nav className="flex items-center gap-2 text-sm text-mutedink">
                            <ThemeToggle />
                            <Link
                                to="/tutor"
                                className="flex items-center gap-2 text-mutedink px-3 py-2 rounded-lg hover:text-ink hover:bg-surface-muted transition-colors"
                            >
                                <MessageSquare size={16} />
                                <span className="hidden sm:inline">Tutor</span>
                            </Link>
                            <Link
                                to="/my-courses"
                                className="flex items-center gap-2 text-mutedink px-3 py-2 rounded-lg hover:text-ink hover:bg-surface-muted transition-colors"
                            >
                                <BookOpen size={16} />
                                <span className="hidden sm:inline">My Courses</span>
                            </Link>
                        </nav>
                    </div>

                    {user && (
                        <div className="flex items-center gap-4">
                            {/* Create Course Button - NOW ENABLED */}
                            <Link 
                                to="/create-course"
                                className="flex items-center gap-2 text-sm text-read px-4 py-2 rounded-lg border border-line hover:bg-surface-muted hover:border-line-strong transition-colors"
                            >
                                <Plus size={16} />
                                <span className="hidden sm:inline">Create Course</span>
                            </Link>

                            {/* User Menu */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 text-sm text-read hover:text-ink focus:outline-none p-2 rounded-lg hover:bg-surface-muted transition-colors"
                                >
                                    <img
                                        src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email)}&background=6366f1&color=fff`}
                                        alt={user.displayName || 'User'}
                                        className="w-8 h-8 rounded-full"
                                    />
                                    <span className="hidden sm:inline font-medium">
                                        {user.displayName || user.email?.split('@')[0] || 'User'}
                                    </span>
                                    <ChevronDown size={16} />
                                </button>

                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-64 bg-surface rounded-xl shadow-lg border border-line py-2 z-50">
                                        {/* User Info */}
                                        <div className="px-4 py-3 border-b border-line">
                                            <p className="text-sm font-medium text-ink">
                                                {user.displayName || 'User'}
                                            </p>
                                            <p className="text-sm text-mutedink truncate">
                                                {user.email}
                                            </p>
                                        </div>
                                        
                                        {/* Menu Items */}
                                        {/* My Courses Menu Item - NOW ENABLED */}
                                        <Link
                                            to="/my-courses"
                                            className="flex items-center gap-3 px-4 py-3 text-sm text-read hover:bg-surface-muted transition-colors"
                                            onClick={() => setShowUserMenu(false)}
                                        >
                                            <BookOpen size={16} />
                                            <div>
                                                <div className="font-medium">My Courses</div>
                                                <div className="text-xs text-mutedink">Manage your courses</div>
                                            </div>
                                        </Link>

                                        <Link
                                            to="/create-course"
                                            className="flex items-center gap-3 px-4 py-3 text-sm text-read hover:bg-surface-muted transition-colors"
                                            onClick={() => setShowUserMenu(false)}
                                        >
                                            <Plus size={16} />
                                            <div>
                                                <div className="font-medium">Create Course</div>
                                                <div className="text-xs text-mutedink">Build custom content</div>
                                            </div>
                                        </Link>
                                        
                                        <div className="border-t border-line mt-2 pt-2">
                                            <button
                                                onClick={handleLogout}
                                                className="flex items-center gap-3 w-full px-4 py-3 text-sm text-read hover:bg-surface-muted transition-colors text-left"
                                            >
                                                <LogOut size={16} />
                                                <div>
                                                    <div className="font-medium">Sign Out</div>
                                                    <div className="text-xs text-mutedink">See you next time!</div>
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Click outside to close menu */}
                    {showUserMenu && (
                        <div
                            className="fixed inset-0 z-40"
                            onClick={() => setShowUserMenu(false)}
                        />
                    )}
                </div>
            </div>
        </header>
    );
}